// src/pages/agent/SubmissionDetail.js
// Lead-submitter view of a single lead they submitted.
// Shows only the timeline steps they're allowed to edit:
//   ① Property Details
//   ② Comparable Sales (RentCast + manual)
// Everything financial (ARV/MAO/contracts/SMS) is admin-only and lives
// in /agent/leads/:id (LeadDetail).
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collectionGroup, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { PageHeader, Card, Btn, BURG_DK, LIGHT, MID, StatusPill, GOLD_PALE } from '../../components/agent/UI';
import { DealTimeline } from './LeadDetail';

export default function SubmissionDetail() {
  const { leadId } = useParams();
  const { profile } = useAgentAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [adminUid, setAdminUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Find the lead in any agent's pipeline that was submitted by this user.
  useEffect(() => {
    if (!profile?.uid || !leadId) return;
    const q = query(
      collectionGroup(db, 'leads'),
      where('submittedBy.uid', '==', profile.uid)
    );
    const unsub = onSnapshot(q, snap => {
      const match = snap.docs.find(d => d.id === leadId);
      if (match) {
        setLead({ id: match.id, ...match.data() });
        // Path: agents/{adminUid}/leads/{leadId} — pull the parent.
        setAdminUid(match.ref.parent.parent.id);
        setError('');
      } else {
        setLead(null);
        setError('We couldn\'t find that submission. It may have been removed.');
      }
      setLoading(false);
    }, err => {
      console.error('submission detail subscribe failed', err);
      setError('Could not load this submission.');
      setLoading(false);
    });
    return unsub;
  }, [profile?.uid, leadId]);

  const update = async (patch) => {
    if (!adminUid || !leadId) return;
    const ref = doc(db, 'agents', adminUid, 'leads', leadId);
    await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
  };

  if (loading) return <div style={{ padding: 40, color: LIGHT }}>Loading submission…</div>;
  if (error) return <div style={{ padding: 40 }}>{error} <a href="/agent/submissions">← My Submissions</a></div>;
  if (!lead) return null;

  const fullAddr = [lead.address?.line1, lead.address?.city, lead.address?.state, lead.address?.zip].filter(Boolean).join(', ');

  return (
    <>
      <PageHeader
        eyebrow="Your submission"
        title={lead.address?.line1 || '(no address)'}
        sub={[lead.address?.city, lead.address?.state, lead.address?.zip].filter(Boolean).join(', ')}
        right={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Btn variant="ghost" onClick={() => navigate('/agent/submissions')}>← My Submissions</Btn>
            <StatusPill status={lead.status} />
          </div>
        }
      />

      <div style={{ background: GOLD_PALE, border: '1px solid rgba(201,168,76,0.4)', borderRadius: 10, padding: '14px 18px', marginBottom: 22, fontSize: 13, color: BURG_DK, lineHeight: 1.65 }}>
        <strong>You can edit two things here</strong> — the property details and the comparable sales. Once you're done, the HomeIQa team takes it from there: setting the ARV, calling the seller, sending contracts. You'll see status updates above as the team works it.
      </div>

      <DealTimeline
        lead={lead}
        update={update}
        profile={profile}
        fullAddr={fullAddr}
        viewMode="lead_submitter"
      />
    </>
  );
}
